// ── urenlijstPdf.js ─────────────────────────────────────────────────────────
// Pure (no React) PDF-generation helpers for the Urenlijst system.
// The PDF layout body is copied verbatim from UrenlijstGenerator.jsx's
// generatePDF() — only the rates source was parameterised (flat RATES →
// a passed-in `rates` object so per-functiegroep tariffs can be applied).

// ── Constants ──────────────────────────────────────────────────────────────────
// Two/three agency identities the urenlijst can be issued under.
export const AGENCIES = {
  ozel: {
    key:   'ozel',
    label: 'ÖZEL UITZENDBUREAU B.V.',
    short: 'Özel',
    name:  'Özel Uitzendbureau BV',
    addr1: 'Schuttevaerweg 81',
    addr2: '3044BA Rotterdam',
    btw:   'NL8160.04.468B01',
    tel:   'tel. 010 415 9100',
    email: 'factuur@ozel.eu',
    vatLines: ['BTW verlegd (Damen)', 'BTW heffen 21% (LBC)'],
  },
  myform: {
    key:   'myform',
    label: 'MYFORM B.V.',
    short: 'Myform',
    name:  'Myform BV',
    addr1: 'Schuttevaerweg 79',
    addr2: '3044BA Rotterdam',
    btw:   'NL8634.45.998B01',
    tel:   '',                    // Myform heeft geen telefoonnummer
    email: 'info@myform.nl',
    vatLines: ['BTW verlegd (Damen)', 'BTW heffen 21% (LBC)'],
  },
  entice: {
    key:   'entice',
    label: 'ENTICE UITZENDBUREAU B.V.',
    short: 'Entice',
    name:  'Entice Uitzendbureau BV',
    addr1: 'Schuttevaerweg 83',
    addr2: '3044BA Rotterdam',
    btw:   'NL8519.44.450B01',
    tel:   'tel. 010 290 93 10',
    email: 'info@enticeuzb.nl',
    vatLines: ['BTW verlegd (Damen)'],
  },
};
export const DEFAULT_AGENCY = AGENCIES.ozel;

const FLAT_RATES = { d: 30, o: 32, an: 32, zat: 34, zon: 34, feest: 34 };

const FEESTDAG_FIELDS = ['ma_fd', 'di_fd', 'wo_fd', 'do_fd', 'vr_fd', 'za_fd', 'zo_fd'];

// ── Helpers ────────────────────────────────────────────────────────────────────
function fmtEuro(v) {
  return '€ ' + (v || 0).toFixed(2).replace('.', ',');
}

// Day value helpers — worker.days.mon can be a number (DB) or {d,o,an} object (Excel)
function gd(day)  { return typeof day === 'object' ? (day?.d  || 0) : (day || 0); }
function go(day)  { return typeof day === 'object' ? (day?.o  || 0) : 0; }
function gan(day) { return typeof day === 'object' ? (day?.an || 0) : 0; }

// Weekly totals — reuse pre-calculated Excel totals, else compute from days.
function computeTotal(worker) {
  const D = worker.days || {};
  const total = worker.total || {
    d:   gd(D.mon) + gd(D.tue) + gd(D.wed) + gd(D.thu) + gd(D.fri),
    o:   go(D.mon) + go(D.tue) + go(D.wed) + go(D.thu) + go(D.fri),
    an:  gan(D.mon) + gan(D.tue) + gan(D.wed) + gan(D.thu) + gan(D.fri),
    zat: typeof D.sat === 'number' ? D.sat : 0,
    zon: typeof D.sun === 'number' ? D.sun : 0,
    gew: 0,
  };
  if (!total.gew) total.gew = total.d + total.o + total.an + total.zat + total.zon;
  return total;
}

// ── CDN script loader ───────────────────────────────────────────────────────────
// Extracted from UrenlijstGenerator's useEffect. Resolves immediately if the
// libraries are already on window (e.g. loaded elsewhere in the app).
let pdfLibsPromise = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      const poll = setInterval(() => {
        if (window.jspdf && window.JSZip) { clearInterval(poll); resolve(); }
      }, 50);
      return;
    }
    const s = document.createElement('script');
    s.src = src; s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });
}

export function loadPdfLibs() {
  if (window.jspdf && window.JSZip) return Promise.resolve();
  if (pdfLibsPromise) return pdfLibsPromise;

  pdfLibsPromise = Promise.all([
    loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js'),
    loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.6.0/jspdf.plugin.autotable.min.js'),
    loadScript('https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js'),
  ]).catch((err) => {
    pdfLibsPromise = null; // allow retry on failure
    throw err;
  });

  return pdfLibsPromise;
}

// ── Functiegroep → rates mapping ────────────────────────────────────────────────
// fg has bedrag_1..7 (or is null/undefined for workers without a functiegroep).
// Exact mapping: d=bedrag_1, o=bedrag_4, an=bedrag_2, zat=bedrag_5, zon=bedrag_6.
export function functiegroepRates(fg) {
  const pick = (v, fallback) => (Number(v) || fallback);
  return {
    d:   pick(fg?.bedrag_1, FLAT_RATES.d),
    o:   pick(fg?.bedrag_4, FLAT_RATES.o),
    an:  pick(fg?.bedrag_2, FLAT_RATES.an),
    zat: pick(fg?.bedrag_5, FLAT_RATES.zat),
    zon: pick(fg?.bedrag_6, FLAT_RATES.zon),
    feest: pick(fg?.bedrag_7, FLAT_RATES.feest),
    btw: fg?.btw ? 0.21 : 0,
  };
}

// ── uren_entries row → worker object expected by generateWorkerPdf ─────────────
export function entryToWorker(entry, meta = {}) {
  return {
    name: meta.name,
    email: meta.email || '',
    btw: meta.btwNumber || '',
    bedrijfsnaam: meta.bedrijfsnaam || '',
    days: {
      mon: { d: +entry.ma_d || 0, o: +entry.ma_o || 0, an: +entry.ma_an || 0 },
      tue: { d: +entry.di_d || 0, o: +entry.di_o || 0, an: +entry.di_an || 0 },
      wed: { d: +entry.wo_d || 0, o: +entry.wo_o || 0, an: +entry.wo_an || 0 },
      thu: { d: +entry.do_d || 0, o: +entry.do_o || 0, an: +entry.do_an || 0 },
      fri: { d: +entry.vr_d || 0, o: +entry.vr_o || 0, an: +entry.vr_an || 0 },
      sat: +entry.za || 0,
      sun: +entry.zo || 0,
    },
    feestdag: FEESTDAG_FIELDS.reduce((s, k) => s + (+entry[k] || 0), 0),
    source: 'uren',
  };
}

// A worker "has hours" if the sum of the 5 day objects' d/o/an + sat + sun > 0.
export function workerHasHours(worker) {
  const D = worker?.days || {};
  const dayTotal = ['mon', 'tue', 'wed', 'thu', 'fri']
    .reduce((sum, k) => sum + gd(D[k]) + go(D[k]) + gan(D[k]), 0);
  return (dayTotal + (D.sat || 0) + (D.sun || 0) + (worker?.feestdag || 0)) > 0;
}

// ── PDF generation ─────────────────────────────────────────────────────────────
// Copied from UrenlijstGenerator.jsx's generatePDF(), parameterised on `rates`
// (per-functiegroep tarieven) and `agency` instead of module-level constants.
export function generateWorkerPdf(worker, opts = {}) {
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  drawWorkerPage(doc, worker, opts);
  return doc;
}

// One combined PDF — every worker on their own page (each with its own rates).
export function generateCombinedPdf(workers, opts = {}) {
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  (workers || []).forEach((w, i) => {
    if (i > 0) doc.addPage();
    drawWorkerPage(doc, w, { ...opts, rates: w.rates || opts.rates });
  });
  return doc;
}

// One combined "totaallijst" — all workers as ROWS in a single table (like the
// original weekurenlijst), hours only, no invoice footer/small-print.
export function generateTotalListPdf(workers, { weekNr, vanDate, tmDate, clientName, agency } = {}) {
  agency = agency || DEFAULT_AGENCY;
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const PW = 297, ML = 10, MR = 10, MT = 10;
  const fb = (v) => (Number(v) > 0 ? String(Number(v)) : '');

  // ── Header ──
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(30, 26, 22);
  doc.text(`week nr.  ${weekNr ?? ''}`, ML, MT + 5);
  doc.setFontSize(13);
  doc.text('weekurenlijst', PW / 2, MT + 5, { align: 'center' });
  doc.setFontSize(11);
  doc.text(agency.label, PW - MR, MT + 5, { align: 'right' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(90, 90, 90);
  if (vanDate) doc.text(`VAN  ${vanDate}`, ML, MT + 11);
  if (tmDate) doc.text(`T/M  ${tmDate}`, ML, MT + 15);
  doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 26, 22);
  doc.text(`Bedrijfsnaam:  ${clientName || ''}`, ML + 42, MT + 13);

  // ── Rows ──
  const sum = { d: 0, o: 0, an: 0, zat: 0, zon: 0, feest: 0, tot: 0 };
  let sumCost = 0, sumBtw = 0;
  const body = (workers || []).map((w, i) => {
    const D = w.days || {};
    const t = computeTotal(w);
    const feest = Number(w.feestdag) || 0;
    const grand = (t.gew || 0) + feest;
    const r = w.rates || FLAT_RATES;
    const cost = t.d * r.d + t.o * r.o + t.an * r.an + t.zat * r.zat + t.zon * r.zon + feest * (r.feest || 0);
    sum.d += t.d; sum.o += t.o; sum.an += t.an; sum.zat += t.zat; sum.zon += t.zon; sum.feest += feest; sum.tot += grand; sumCost += cost;
    const btw = cost * (r.btw || 0);
    sumBtw += btw;
    return [
      String(i + 1), w.name,
      fb(gd(D.mon)), fb(go(D.mon)), fb(gan(D.mon)),
      fb(gd(D.tue)), fb(go(D.tue)), fb(gan(D.tue)),
      fb(gd(D.wed)), fb(go(D.wed)), fb(gan(D.wed)),
      fb(gd(D.thu)), fb(go(D.thu)), fb(gan(D.thu)),
      fb(gd(D.fri)), fb(go(D.fri)), fb(gan(D.fri)),
      fb(D.sat), fb(D.sun),
      fb(t.d), fb(t.o), fb(t.an), fb(t.zat), fb(t.zon), fb(feest), fb(grand),
      { content: fmtEuro(cost), styles: { halign: 'right' } },
      { content: btw ? fmtEuro(btw) : '—', styles: { halign: 'right' } },
      { content: fmtEuro(cost + btw), styles: { halign: 'right' } },
    ];
  });
  body.push([
    '', 'totaal uren',
    '', '', '', '', '', '', '', '', '', '', '', '', '', '', '',
    '', '',
    fb(sum.d), fb(sum.o), fb(sum.an), fb(sum.zat), fb(sum.zon), fb(sum.feest), fb(sum.tot),
    '', '', '',
  ]);
  body.push([
    '', 'totaal kosten',
    '', '', '', '', '', '', '', '', '', '', '', '', '', '', '',
    '', '',
    '', '', '', '', '', '', '',
    { content: fmtEuro(sumCost), styles: { halign: 'right', fontStyle: 'bold' } },
    { content: fmtEuro(sumBtw), styles: { halign: 'right', fontStyle: 'bold' } },
    { content: fmtEuro(sumCost + sumBtw), styles: { halign: 'right', fontStyle: 'bold' } },
  ]);

  const head = [
    [
      { content: 'nr', rowSpan: 2 }, { content: 'Naam en voorletters', rowSpan: 2 },
      { content: 'Maandag', colSpan: 3 }, { content: 'Dinsdag', colSpan: 3 }, { content: 'Woensdag', colSpan: 3 },
      { content: 'Donderdag', colSpan: 3 }, { content: 'Vrijdag', colSpan: 3 },
      { content: 'Zat', rowSpan: 2 }, { content: 'Zon', rowSpan: 2 },
      { content: 'D', rowSpan: 2 }, { content: 'O', rowSpan: 2 }, { content: 'A/N', rowSpan: 2 },
      { content: 'ZAT', rowSpan: 2 }, { content: 'ZON', rowSpan: 2 }, { content: 'Feest', rowSpan: 2 }, { content: 'Totaal', rowSpan: 2 },
      { content: 'Kosten', rowSpan: 2 }, { content: 'Btw 21%', rowSpan: 2 }, { content: 'Totale kosten', rowSpan: 2 },
    ],
    ['d', 'o', 'a/n', 'd', 'o', 'a/n', 'd', 'o', 'a/n', 'd', 'o', 'a/n', 'd', 'o', 'a/n'],
  ];

  doc.autoTable({
    head, body, startY: MT + 18, margin: { left: ML, right: MR },
    styles: { fontSize: 7, cellPadding: { top: 1, right: 0.6, bottom: 1, left: 0.6 }, halign: 'center', lineColor: [200, 200, 200], lineWidth: 0.15, textColor: [30, 26, 22], overflow: 'visible' },
    headStyles: { fillColor: [235, 235, 235], textColor: [40, 40, 40], fontStyle: 'bold', fontSize: 6.5, halign: 'center' },
    columnStyles: { 0: { cellWidth: 7 }, 1: { halign: 'left', cellWidth: 30 }, 26: { cellWidth: 18, halign: 'right' }, 27: { cellWidth: 16, halign: 'right' }, 28: { cellWidth: 20, halign: 'right' } },
    theme: 'grid',
    didParseCell(d) {
      // Style the two footer rows (totaal uren + totaal kosten).
      if (d.section === 'body' && d.row.index >= body.length - 2) {
        d.cell.styles.fontStyle = 'bold';
        d.cell.styles.fillColor = [244, 244, 244];
      }
    },
  });

  return doc;
}

function drawWorkerPage(doc, worker, { weekNr, vanDate, tmDate, clientName, agency, rates } = {}) {
  agency = agency || DEFAULT_AGENCY;
  rates = rates || FLAT_RATES;

  const PW = 297, PH = 210;
  const ML = 12, MR = 12, MT = 12;

  const vanDateStr = vanDate || '';
  const tmDateStr  = tmDate  || '';

  const D = worker.days;

  // Use pre-calculated totals from Excel if available, else compute from days
  const total = computeTotal(worker);

  const feestdagUren = Number(worker.feestdag) || 0;
  const amt = {
    d:   total.d   * rates.d,
    o:   total.o   * rates.o,
    an:  total.an  * rates.an,
    zat: total.zat * rates.zat,
    zon: total.zon * rates.zon,
    feest: feestdagUren * (rates.feest || 0),
  };
  amt.total = amt.d + amt.o + amt.an + amt.zat + amt.zon + amt.feest;

  // Hours: trim trailing zeros so values fit one line in the narrow columns (11.50 → 11.5).
  const fb = v => { const n = parseFloat(v); return n > 0 ? String(n) : ''; };
  // Amounts under the hours: plain number, NO euro sign (euro only on the eindbedrag).
  const fmtNum = v => { const n = Number(v); return n ? n.toFixed(2).replace('.', ',') : ''; };

  // ── Header box ────────────────────────────────────────────────────────────────
  doc.setFillColor(245, 242, 236);
  doc.rect(ML, MT, PW - ML - MR, 26, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(180, 100, 20);
  doc.text(`WEEK ${weekNr}`, ML + 4, MT + 7);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(30, 26, 22);
  doc.text('WEEKURENLIJST', ML + 4, MT + 15);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(100, 95, 88);
  doc.text(`${vanDateStr} — ${tmDateStr}`, ML + 4, MT + 22);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(30, 26, 22);
  doc.text(agency.label, PW - MR - 4, MT + 10, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(100, 95, 88);
  doc.text(agency.addr1 + ', ' + agency.addr2, PW - MR - 4, MT + 16, { align: 'right' });
  doc.text([agency.tel, agency.email].filter(Boolean).join('  ·  '), PW - MR - 4, MT + 21, { align: 'right' });

  // ── Worker / Client info row ──────────────────────────────────────────────────
  const infoY = MT + 30;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(100, 95, 88);
  doc.text('MEDEWERKER', ML, infoY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(30, 26, 22);
  doc.text(worker.name, ML, infoY + 5.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(100, 95, 88);
  doc.text('TARIEF/UUR', ML + 70, infoY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(30, 26, 22);
  doc.text(worker.hourlyRateOut > 0 ? fmtEuro(worker.hourlyRateOut) : '—', ML + 70, infoY + 5.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(100, 95, 88);
  doc.text('OPDRACHTGEVER', PW - MR - 70, infoY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(30, 26, 22);
  doc.text(clientName || '—', PW - MR - 70, infoY + 5.5);

  // Second info row — worker's own bedrijfsnaam / e-mail / BTW-nummer (from DB)
  const infoY2 = infoY + 13;
  const label2 = (lbl, val, x) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 95, 88);
    doc.text(lbl, x, infoY2);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(30, 26, 22);
    doc.text(val || '—', x, infoY2 + 5);
  };
  label2('BEDRIJFSNAAM', worker.bedrijfsnaam, ML);
  label2('E-MAIL', worker.email, ML + 70);
  label2('BTW-NUMMER', worker.btw, PW - MR - 70);

  doc.setDrawColor(210, 205, 195);
  doc.setLineWidth(0.3);
  doc.line(ML, infoY + 24, PW - MR, infoY + 24);

  // ── Hours table ───────────────────────────────────────────────────────────────
  const tableY = infoY + 27;

  const cw = [12, 38,  8,8,8,  8,8,8,  8,8,8,  8,8,8,  8,8,8,  11, 11,  12,12,12,12,12, 16];
  const colStyles = Object.fromEntries(cw.map((w, i) => [i, { cellWidth: w, halign: i > 1 ? 'center' : 'left' }]));

  const headRows = [
    [
      { content: 'nr',          rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
      { content: 'Naam',        rowSpan: 2, styles: { valign: 'middle' } },
      { content: 'Maandag',   colSpan: 3,  styles: { halign: 'center' } },
      { content: 'Dinsdag',   colSpan: 3,  styles: { halign: 'center' } },
      { content: 'Woensdag',  colSpan: 3,  styles: { halign: 'center' } },
      { content: 'Donderdag', colSpan: 3,  styles: { halign: 'center' } },
      { content: 'Vrijdag',   colSpan: 3,  styles: { halign: 'center' } },
      { content: 'Zat',  rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
      { content: 'Zon',  rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
      { content: 'D',    rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
      { content: 'O',    rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
      { content: 'A/N',  rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
      { content: 'ZAT',  rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
      { content: 'ZON',  rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
      { content: 'Totaal', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
    ],
    [
      ...['d','o','a/n','d','o','a/n','d','o','a/n','d','o','a/n','d','o','a/n']
        .map(s => ({ content: s, styles: { halign: 'center', fontStyle: 'italic' } })),
    ],
  ];

  const R = rates;
  const bodyRows = [
    // Hours row — full d/o/an per day
    [
      'DSR', worker.name,
      fb(gd(D.mon)), fb(go(D.mon)), fb(gan(D.mon)),
      fb(gd(D.tue)), fb(go(D.tue)), fb(gan(D.tue)),
      fb(gd(D.wed)), fb(go(D.wed)), fb(gan(D.wed)),
      fb(gd(D.thu)), fb(go(D.thu)), fb(gan(D.thu)),
      fb(gd(D.fri)), fb(go(D.fri)), fb(gan(D.fri)),
      fb(typeof D.sat === 'number' ? D.sat : 0),
      fb(typeof D.sun === 'number' ? D.sun : 0),
      fb(total.d), fb(total.o), fb(total.an), fb(total.zat), fb(total.zon), '',
    ],
    // Rates row
    [
      { content: 'tarieven', styles: { textColor: [150,145,138], fontStyle: 'italic', fontSize: 6.5 } },
      '', '','','', '','','', '','','', '','','', '','','', '','',
      R.d, R.o, R.an, R.zat, R.zon, '',
    ],
    // Amounts row
    [
      { content: 'bedragen', styles: { textColor: [150,145,138], fontStyle: 'italic', fontSize: 6.5 } },
      { content: '', styles: {} },
      '','','', '','','', '','','', '','','', '','','', '','',
      fmtNum(amt.d), fmtNum(amt.o), fmtNum(amt.an), fmtNum(amt.zat), fmtNum(amt.zon),
      { content: fmtNum(amt.total), styles: { fontStyle: 'bold', textColor: [180, 100, 20] } },
    ],
    // Feestdag — hours (per-day total) at the feestdag rate (bedrag_7)
    [
      { content: 'feestdag', styles: { textColor: [150,145,138], fontStyle: 'italic', fontSize: 6.5 } },
      '', '','','', '','','', '','','', '','','', '','','', '','',
      fb(feestdagUren), '', '', '', '',
      amt.feest ? { content: fmtNum(amt.feest), styles: { fontStyle: 'bold', textColor: [180, 100, 20] } } : '',
    ],
    [
      { content: 'tarief feestdag', styles: { textColor: [150,145,138], fontStyle: 'italic', fontSize: 6.5 } },
      '', '','','', '','','', '','','', '','','', '','','', '','',
      rates.feest, '', '', '', '', '',
    ],
  ];

  doc.autoTable({
    head: headRows,
    body: bodyRows,
    startY: tableY,
    margin: { left: ML, right: MR },
    styles: {
      fontSize: 7.5,
      cellPadding: { top: 1.6, right: 0.8, bottom: 1.6, left: 0.8 },
      overflow: 'visible',
      lineColor: [210, 205, 195],
      lineWidth: 0.2,
      textColor: [30, 26, 22],
    },
    headStyles: {
      fillColor: [230, 225, 215],
      textColor: [30, 26, 22],
      fontStyle: 'bold',
      fontSize: 7,
      lineColor: [210, 205, 195],
      lineWidth: 0.2,
    },
    bodyStyles: { fillColor: [255, 255, 255] },
    alternateRowStyles: { fillColor: [255, 255, 255] },
    columnStyles: colStyles,
    theme: 'grid',
    didParseCell(data) {
      if (data.section === 'body' && data.row.index === 1) {
        data.cell.styles.fillColor = [248, 245, 238];
      }
      if (data.section === 'body' && data.row.index === 2) {
        data.cell.styles.fillColor = [252, 248, 242];
      }
      if (data.section === 'body' && (data.row.index === 3 || data.row.index === 4)) {
        data.cell.styles.fillColor = [250, 250, 250];
        data.cell.styles.textColor = [160, 155, 148];
      }
    },
  });

  // ── Footer ────────────────────────────────────────────────────────────────────
  const footY = doc.lastAutoTable.finalY + 7;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(30, 26, 22);
  doc.text(agency.name, ML, footY);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(100, 95, 88);
  [
    agency.addr1,
    agency.addr2,
    'Btwnr: ' + agency.btw,
    ...(agency.vatLines || ['BTW verlegd (Damen)', 'BTW heffen 21% (LBC)']),
  ].forEach((line, i) => doc.text(line, ML, footY + 5 + i * 4.5));

  const btwPct = rates.btw || 0;
  const btwAmt = amt.total * btwPct;

  // BTW notice box
  doc.setFillColor(255, 248, 238);
  doc.setDrawColor(212, 120, 15);
  doc.setLineWidth(0.3);
  const noticeX = ML + 70, noticeW = 130, noticeY = footY - 1;
  doc.roundedRect(noticeX, noticeY, noticeW, 24, 1.5, 1.5, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(180, 100, 20);
  if (btwPct) {
    doc.text('LET OP: 21% BTW BEREKENEN', noticeX + 4, noticeY + 5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.8);
    doc.setTextColor(80, 60, 30);
    doc.text('Op deze factuur moet 21% btw berekend worden over het bedrag.', noticeX + 4, noticeY + 10);
    doc.text(`Bedrag ${fmtEuro(amt.total)}  +  btw 21% ${fmtEuro(btwAmt)}  =  ${fmtEuro(amt.total + btwAmt)}`, noticeX + 4, noticeY + 15);
  } else {
  doc.text('LET OP: BTW VERLEGGING', noticeX + 4, noticeY + 5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.8);
  doc.setTextColor(80, 60, 30);
  doc.text('Op de facturen waarbij de BTW is verlegd moet de onderstaande', noticeX + 4, noticeY + 10);
  doc.text('regel toegevoegd worden:', noticeX + 4, noticeY + 14.5);
  doc.setFont('helvetica', 'italic');
  doc.text(`"Op deze factuur geldt een btw verleggingsregel naar btw nummer`, noticeX + 4, noticeY + 19);
  doc.text(`${agency.btw} namens ${agency.name}"`, noticeX + 4, noticeY + 23);
  }

  // Totaal block (right)
  const totX = PW - MR - 55;
  doc.setFillColor(212, 120, 15);
  doc.rect(totX, footY - 1, 55, 14, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(255, 255, 255);
  doc.text('TOTAAL FACTUUR', totX + 4, footY + 5);

  doc.setFontSize(14);
  doc.text(fmtEuro(amt.total + btwAmt), totX + 4, footY + 12);
  if (btwPct) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(80, 60, 30);
    doc.text(`excl. btw  ${fmtEuro(amt.total)}`, totX + 2, footY + 17);
    doc.text(`btw 21%   ${fmtEuro(btwAmt)}`, totX + 2, footY + 21);
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(100, 95, 88);
  doc.text(
    `Graag de factuur opmaken zodra u de werkurenbrief heeft ontvangen  ·  ${agency.email}`,
    ML, PH - MR - 2
  );
}

// ── Purchase Order ─────────────────────────────────────────────────────────
// Zelfde urentabel als de totaallijst, maar met een PO-kop (gegevens van het
// bedrijf links, "Purchase Order" + weeknummer midden, onze gegevens rechts) en
// betalingstermijn onderaan. De bedragen komen NIET uit de functiegroep maar uit
// het gekozen bedrijf: één tariefset die voor iedereen op deze PO geldt.
export function poRates(bedrijf) {
  const n = (v) => (Number(v) > 0 ? Number(v) : 0);
  return {
    d:     n(bedrijf?.tarief_dag),
    o:     n(bedrijf?.tarief_overwerk),
    an:    n(bedrijf?.tarief_avondnacht),
    zat:   n(bedrijf?.tarief_zaterdag),
    zon:   n(bedrijf?.tarief_zondag),
    feest: n(bedrijf?.tarief_feestdag),
  };
}

export function generatePurchaseOrderPdf(workers, { weekNr, jaar, vanDate, tmDate, agency, bedrijf } = {}) {
  agency = agency || DEFAULT_AGENCY;
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const PW = 297, ML = 10, MR = 10, MT = 10;
  const fb = (v) => (Number(v) > 0 ? String(Number(v)) : '');
  const rates = poRates(bedrijf);

  // ── Kop: bedrijf links, titel midden, wij rechts ──
  doc.setTextColor(30, 26, 22);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  doc.text('AAN', ML, MT + 4);
  doc.setFontSize(10);
  doc.text(String(bedrijf?.naam || ''), ML, MT + 9);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(90, 90, 90);
  let ly = MT + 13.5;
  for (const line of [bedrijf?.adres, [bedrijf?.postcode, bedrijf?.plaats].filter(Boolean).join('  '),
                      bedrijf?.kvk ? `KvK ${bedrijf.kvk}` : '',
                      bedrijf?.btw_nummer ? `BTW ${bedrijf.btw_nummer}` : '']) {
    if (line) { doc.text(String(line), ML, ly); ly += 4; }
  }

  doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.setTextColor(30, 26, 22);
  doc.text('Purchase Order', PW / 2, MT + 6, { align: 'center' });
  doc.setFontSize(9.5);
  doc.text(`Weeknummer: ${weekNr ?? ''}${jaar ? ` — ${jaar}` : ''}`, PW / 2, MT + 12, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(90, 90, 90);
  if (vanDate || tmDate) {
    doc.text(`${vanDate || ''}${vanDate && tmDate ? '  t/m  ' : ''}${tmDate || ''}`, PW / 2, MT + 17, { align: 'center' });
  }

  doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(30, 26, 22);
  doc.text(agency.label, PW - MR, MT + 6, { align: 'right' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(90, 90, 90);
  let ry = MT + 11;
  for (const line of [agency.addr1, agency.addr2, `BTW ${agency.btw}`, agency.tel, agency.email]) {
    if (line) { doc.text(String(line), PW - MR, ry, { align: 'right' }); ry += 4; }
  }

  // ── Urentabel (zelfde opzet als de totaallijst) ──
  const sum = { d: 0, o: 0, an: 0, zat: 0, zon: 0, feest: 0, tot: 0 };
  let sumCost = 0;
  const body = (workers || []).map((w, i) => {
    const D = w.days || {};
    const t = computeTotal(w);
    const feest = Number(w.feestdag) || 0;
    const grand = (t.gew || 0) + feest;
    const cost = t.d * rates.d + t.o * rates.o + t.an * rates.an
               + t.zat * rates.zat + t.zon * rates.zon + feest * rates.feest;
    sum.d += t.d; sum.o += t.o; sum.an += t.an; sum.zat += t.zat; sum.zon += t.zon;
    sum.feest += feest; sum.tot += grand; sumCost += cost;
    return [
      String(i + 1), w.name,
      fb(gd(D.mon)), fb(go(D.mon)), fb(gan(D.mon)),
      fb(gd(D.tue)), fb(go(D.tue)), fb(gan(D.tue)),
      fb(gd(D.wed)), fb(go(D.wed)), fb(gan(D.wed)),
      fb(gd(D.thu)), fb(go(D.thu)), fb(gan(D.thu)),
      fb(gd(D.fri)), fb(go(D.fri)), fb(gan(D.fri)),
      fb(D.sat), fb(D.sun),
      fb(t.d), fb(t.o), fb(t.an), fb(t.zat), fb(t.zon), fb(feest), fb(grand),
      { content: fmtEuro(cost), styles: { halign: 'right' } },
    ];
  });
  const blanks = (n) => Array(n).fill('');
  body.push(['', 'totaal uren', ...blanks(17),
    fb(sum.d), fb(sum.o), fb(sum.an), fb(sum.zat), fb(sum.zon), fb(sum.feest), fb(sum.tot), '']);
  body.push(['', 'totaal kosten', ...blanks(17), ...blanks(7),
    { content: fmtEuro(sumCost), styles: { halign: 'right', fontStyle: 'bold' } }]);

  const head = [
    [
      { content: 'nr', rowSpan: 2 }, { content: 'Naam en voorletters', rowSpan: 2 },
      { content: 'Maandag', colSpan: 3 }, { content: 'Dinsdag', colSpan: 3 }, { content: 'Woensdag', colSpan: 3 },
      { content: 'Donderdag', colSpan: 3 }, { content: 'Vrijdag', colSpan: 3 },
      { content: 'Zat', rowSpan: 2 }, { content: 'Zon', rowSpan: 2 },
      { content: 'D', rowSpan: 2 }, { content: 'O', rowSpan: 2 }, { content: 'A/N', rowSpan: 2 },
      { content: 'ZAT', rowSpan: 2 }, { content: 'ZON', rowSpan: 2 }, { content: 'Feest', rowSpan: 2 },
      { content: 'Totaal', rowSpan: 2 }, { content: 'Bedrag', rowSpan: 2 },
    ],
    ['d', 'o', 'a/n', 'd', 'o', 'a/n', 'd', 'o', 'a/n', 'd', 'o', 'a/n', 'd', 'o', 'a/n'],
  ];

  doc.autoTable({
    head, body, startY: MT + 32, margin: { left: ML, right: MR },
    styles: { fontSize: 7, cellPadding: { top: 1, right: 0.6, bottom: 1, left: 0.6 }, halign: 'center', lineColor: [200, 200, 200], lineWidth: 0.15, textColor: [30, 26, 22], overflow: 'visible' },
    headStyles: { fillColor: [235, 235, 235], textColor: [40, 40, 40], fontStyle: 'bold', fontSize: 6.5, halign: 'center' },
    columnStyles: { 0: { cellWidth: 7 }, 1: { halign: 'left', cellWidth: 30 }, 26: { cellWidth: 22, halign: 'right' } },
    theme: 'grid',
    didParseCell(d) {
      if (d.section === 'body' && d.row.index >= body.length - 2) {
        d.cell.styles.fontStyle = 'bold';
        d.cell.styles.fillColor = [244, 244, 244];
      }
    },
  });

  // ── Betalingstermijn ──
  const endY = (doc.lastAutoTable?.finalY || MT + 40) + 8;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(30, 26, 22);
  doc.text('Betaling binnen 30 dagen', ML, endY);

  return doc;
}
